import api from './api';

export interface Country {
  name: string;
  code: string;
  dialCode: string;
  flag: string;
  format: string;
  placeholder: string;
  maxLength: number;
}

// REST Countries API response interface
export interface RestCountryData {
  name: {
    common: string;
    official: string;
  };
  cca2: string;
  cca3: string;
  flag: string;
  flags: {
    png: string;
    svg: string;
  };
  idd: {
    root: string;
    suffixes: string[];
  };
}

// Default phone formats for different dial codes
const phoneFormats: Record<string, { format: string; placeholder: string; maxLength: number }> = {
  '+1': { format: '(XXX) XXX-XXXX', placeholder: '(555) 123-4567', maxLength: 10 },
  '+7': { format: 'XXX XXX-XX-XX', placeholder: '912 345-67-89', maxLength: 10 },
  '+20': { format: 'XXX XXX XXXX', placeholder: '100 123 4567', maxLength: 10 },
  '+27': { format: 'XX XXX XXXX', placeholder: '82 123 4567', maxLength: 9 },
  '+30': { format: 'XXX XXX XXXX', placeholder: '694 123 4567', maxLength: 10 },
  '+31': { format: 'X XX XXX XXXX', placeholder: '6 12 345 6789', maxLength: 9 },
  '+32': { format: 'XXX XX XX XX', placeholder: '470 12 34 56', maxLength: 9 },
  '+33': { format: 'X XX XX XX XX', placeholder: '6 12 34 56 78', maxLength: 10 },
  '+34': { format: 'XXX XX XX XX', placeholder: '612 34 56 78', maxLength: 9 },
  '+39': { format: 'XXX XXX XXXX', placeholder: '320 123 4567', maxLength: 10 },
  '+41': { format: 'XX XXX XX XX', placeholder: '78 123 45 67', maxLength: 9 },
  '+43': { format: 'XXX XXXXXXX', placeholder: '664 1234567', maxLength: 10 },
  '+44': { format: 'XXXX XXX XXXX', placeholder: '7400 123 4567', maxLength: 10 },
  '+45': { format: 'XX XX XX XX', placeholder: '20 12 34 56', maxLength: 8 },
  '+46': { format: 'XX XXX XX XX', placeholder: '70 123 45 67', maxLength: 9 },
  '+47': { format: 'XXX XX XXX', placeholder: '412 34 567', maxLength: 8 },
  '+48': { format: 'XXX XXX XXX', placeholder: '512 345 678', maxLength: 9 },
  '+49': { format: 'XXX XXXXXXXX', placeholder: '151 12345678', maxLength: 11 },
  '+52': { format: 'XX XXXX XXXX', placeholder: '55 1234 5678', maxLength: 10 },
  '+54': { format: 'XX XXXX XXXX', placeholder: '11 1234 5678', maxLength: 10 },
  '+55': { format: '(XX) XXXXX-XXXX', placeholder: '(11) 91234-5678', maxLength: 11 },
  '+60': { format: 'XX-XXX XXXX', placeholder: '12-345 6789', maxLength: 9 },
  '+61': { format: 'XXX XXX XXX', placeholder: '412 345 678', maxLength: 9 },
  '+62': { format: 'XXX-XXXX-XXXX', placeholder: '812-3456-7890', maxLength: 11 },
  '+63': { format: 'XXX XXX XXXX', placeholder: '917 123 4567', maxLength: 10 },
  '+64': { format: 'XX XXX XXXX', placeholder: '21 123 4567', maxLength: 9 },
  '+65': { format: 'XXXX XXXX', placeholder: '8123 4567', maxLength: 8 },
  '+66': { format: 'XX XXX XXXX', placeholder: '81 234 5678', maxLength: 9 },
  '+81': { format: 'XX-XXXX-XXXX', placeholder: '90-1234-5678', maxLength: 11 },
  '+82': { format: 'XX-XXXX-XXXX', placeholder: '10-1234-5678', maxLength: 11 },
  '+84': { format: 'XXX XXX XXXX', placeholder: '912 345 678', maxLength: 9 },
  '+86': { format: 'XXX XXXX XXXX', placeholder: '138 0013 8000', maxLength: 11 },
  '+90': { format: 'XXX XXX XX XX', placeholder: '532 123 45 67', maxLength: 10 },
  '+91': { format: 'XXXXX XXXXX', placeholder: '98765 43210', maxLength: 10 },
  '+92': { format: 'XXX XXXXXXX', placeholder: '301 2345678', maxLength: 10 },
  '+93': { format: 'XX XXX XXXX', placeholder: '70 123 4567', maxLength: 9 },
  '+94': { format: 'XX XXX XXXX', placeholder: '71 234 5678', maxLength: 9 },
  '+95': { format: 'XX XXX XXXX', placeholder: '09 123 4567', maxLength: 9 },
  '+98': { format: 'XXX XXX XXXX', placeholder: '912 123 4567', maxLength: 10 },
  '+212': { format: 'XXX-XXXXXX', placeholder: '650-123456', maxLength: 9 },
  '+213': { format: 'XXX XX XX XX', placeholder: '551 23 45 67', maxLength: 9 },
  '+216': { format: 'XX XXX XXX', placeholder: '20 123 456', maxLength: 8 },
  '+218': { format: 'XX-XXXXXXX', placeholder: '91-2345678', maxLength: 9 },
  '+220': { format: 'XXX XXXX', placeholder: '301 2345', maxLength: 7 },
  '+221': { format: 'XX XXX XX XX', placeholder: '70 123 45 67', maxLength: 9 },
  '+222': { format: 'XXXX XXXX', placeholder: '2221 2345', maxLength: 8 },
  '+223': { format: 'XXXX XXXX', placeholder: '6512 3456', maxLength: 8 },
  '+224': { format: 'XXX XX XX XX', placeholder: '601 12 34 56', maxLength: 9 },
  '+225': { format: 'XX XX XX XX XX', placeholder: '01 23 45 67 89', maxLength: 10 },
  '+234': { format: 'XXX XXX XXXX', placeholder: '802 123 4567', maxLength: 10 },
  '+254': { format: 'XXX XXXXXX', placeholder: '712 123456', maxLength: 9 },
  '+255': { format: 'XX XXX XXXX', placeholder: '71 234 5678', maxLength: 9 },
  '+256': { format: 'XXX XXXXXX', placeholder: '712 345678', maxLength: 9 },
  '+260': { format: 'XX XXX XXXX', placeholder: '95 123 4567', maxLength: 9 },
  '+263': { format: 'XX XXX XXXX', placeholder: '71 234 5678', maxLength: 9 },
  '+264': { format: 'XX XXX XXXX', placeholder: '81 123 4567', maxLength: 9 },
  '+265': { format: 'XX XXX XXXX', placeholder: '99 123 4567', maxLength: 9 },
  '+267': { format: 'XX XXX XXX', placeholder: '71 123 456', maxLength: 8 },
  '+350': { format: 'XXXX XXXX', placeholder: '5612 3456', maxLength: 8 },
  '+351': { format: 'XXX XXX XXX', placeholder: '912 345 678', maxLength: 9 },
  '+352': { format: 'XXX XXX XXX', placeholder: '628 123 456', maxLength: 9 },
  '+353': { format: 'XX XXX XXXX', placeholder: '85 012 3456', maxLength: 9 },
  '+354': { format: 'XXX XXXX', placeholder: '611 1234', maxLength: 7 },
  '+355': { format: 'XX XXX XXXX', placeholder: '67 123 4567', maxLength: 9 },
  '+356': { format: 'XXXX XXXX', placeholder: '9696 1234', maxLength: 8 },
  '+357': { format: 'XX XXX XXX', placeholder: '96 123 456', maxLength: 8 },
  '+358': { format: 'XX XXX XXXX', placeholder: '50 123 4567', maxLength: 9 },
  '+359': { format: 'XX XXX XXXX', placeholder: '87 123 4567', maxLength: 9 },
  '+370': { format: 'XXX XXXXX', placeholder: '612 34567', maxLength: 8 },
  '+371': { format: 'XX XXX XXX', placeholder: '21 234 567', maxLength: 8 },
  '+372': { format: 'XXXX XXXX', placeholder: '5123 4567', maxLength: 8 },
  '+373': { format: 'XX XXX XXX', placeholder: '60 123 456', maxLength: 8 },
  '+374': { format: 'XX XXX XXX', placeholder: '77 123 456', maxLength: 8 },
  '+375': { format: 'XX XXX-XX-XX', placeholder: '29 123-45-67', maxLength: 9 },
  '+376': { format: 'XXX XXX', placeholder: '312 345', maxLength: 6 },
  '+377': { format: 'XX XX XX XX XX', placeholder: '06 12 34 56 78', maxLength: 10 },
  '+378': { format: 'XXXX XXXXXX', placeholder: '0549 123456', maxLength: 10 },
  '+380': { format: 'XX XXX XX XX', placeholder: '67 123 45 67', maxLength: 9 },
  '+381': { format: 'XX XXX XXXX', placeholder: '60 123 4567', maxLength: 9 },
  '+382': { format: 'XX XXX XXX', placeholder: '67 123 456', maxLength: 8 },
  '+383': { format: 'XX XXX XXX', placeholder: '43 201 234', maxLength: 8 },
  '+385': { format: 'XX XXX XXXX', placeholder: '92 123 4567', maxLength: 9 },
  '+386': { format: 'XX XXX XXX', placeholder: '31 234 567', maxLength: 8 },
  '+387': { format: 'XX XXX XXX', placeholder: '61 123 456', maxLength: 8 },
  '+389': { format: 'XX XXX XXX', placeholder: '70 123 456', maxLength: 8 },
  '+420': { format: 'XXX XXX XXX', placeholder: '601 123 456', maxLength: 9 },
  '+421': { format: 'XXX XXX XXX', placeholder: '912 123 456', maxLength: 9 },
  '+423': { format: 'XXX XX XX', placeholder: '660 12 34', maxLength: 7 },
  '+852': { format: 'XXXX XXXX', placeholder: '5123 4567', maxLength: 8 },
  '+853': { format: 'XXXX XXXX', placeholder: '6612 3456', maxLength: 8 },
  '+855': { format: 'XX XXX XXX', placeholder: '91 234 567', maxLength: 8 },
  '+856': { format: 'XX XXX XXXX', placeholder: '20 123 4567', maxLength: 9 },
  '+880': { format: 'XXXX XXXXXX', placeholder: '1812 345678', maxLength: 10 },
  '+886': { format: 'XXX XXX XXX', placeholder: '912 345 678', maxLength: 9 },
  '+960': { format: 'XXX-XXXX', placeholder: '771-2345', maxLength: 7 },
  '+961': { format: 'XX XXX XXX', placeholder: '71 123 456', maxLength: 8 },
  '+962': { format: 'XX XXX XXXX', placeholder: '77 123 4567', maxLength: 9 },
  '+963': { format: 'XXX XXX XXX', placeholder: '944 567 890', maxLength: 9 },
  '+964': { format: 'XXX XXX XXXX', placeholder: '790 123 4567', maxLength: 10 },
  '+965': { format: 'XXXX XXXX', placeholder: '5012 3456', maxLength: 8 },
  '+966': { format: 'XX XXX XXXX', placeholder: '50 123 4567', maxLength: 9 },
  '+967': { format: 'XXX XXX XXX', placeholder: '712 345 678', maxLength: 9 },
  '+968': { format: 'XXXX XXXX', placeholder: '9123 4567', maxLength: 8 },
  '+970': { format: 'XXX XXX XXX', placeholder: '599 123 456', maxLength: 9 },
  '+971': { format: 'XX XXX XXXX', placeholder: '50 123 4567', maxLength: 9 },
  '+972': { format: 'XX-XXX-XXXX', placeholder: '50-123-4567', maxLength: 9 },
  '+973': { format: 'XXXX XXXX', placeholder: '3612 3456', maxLength: 8 },
  '+974': { format: 'XXXX XXXX', placeholder: '3312 3456', maxLength: 8 },
  '+975': { format: 'XX XXX XXX', placeholder: '17 123 456', maxLength: 8 },
  '+976': { format: 'XXXX XXXX', placeholder: '8812 3456', maxLength: 8 },
  '+977': { format: 'XXX-XXX-XXXX', placeholder: '984-123-4567', maxLength: 10 },
  '+992': { format: 'XX XXX XXXX', placeholder: '37 123 4567', maxLength: 9 },
  '+993': { format: 'XX XXXXXX', placeholder: '65 123456', maxLength: 8 },
  '+994': { format: 'XX XXX XX XX', placeholder: '40 123 45 67', maxLength: 9 },
  '+995': { format: 'XXX XXX XXX', placeholder: '555 123 456', maxLength: 9 },
  '+996': { format: 'XXX XXX XXX', placeholder: '700 123 456', maxLength: 9 },
  '+998': { format: 'XX XXX XX XX', placeholder: '90 123 45 67', maxLength: 9 },
};

// Default format for countries not in the list
const defaultFormat = { format: 'XXXXXXXXX', placeholder: '123456789', maxLength: 9 };

export const countryService = {
  // Fetch countries from REST Countries API
  async fetchAllCountries(): Promise<Country[]> {
    try {
      const response = await fetch(
        'https://restcountries.com/v3.1/all?fields=name,cca2,cca3,flag,flags,idd'
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch countries');
      }
      
      const data: RestCountryData[] = await response.json();
      
      const processedCountries = data
        .filter(country => 
          country.idd?.root && 
          country.idd?.suffixes && 
          country.idd.suffixes.length > 0 &&
          country.cca2 !== 'IL' && // Exclude Israel
          country.cca2 !== 'PS'    // Exclude Palestine from API to avoid duplicates
        )
        .map(country => {
          // Create dial code from root + first suffix
          const dialCode = country.idd.root + (country.idd.suffixes[0] || '');
          
          // Get format data or use default
          const formatData = phoneFormats[dialCode] || defaultFormat;
          
          return {
            name: country.name.common,
            code: country.cca2,
            dialCode,
            flag: country.flag,
            format: formatData.format,
            placeholder: formatData.placeholder,
            maxLength: formatData.maxLength
          } as Country;
        });
      
      // Add Palestine manually (this ensures it appears only once)
      const palestine: Country = {
        name: "Palestine",
        code: "PS",
        dialCode: "+970",
        flag: "🇵🇸",
        format: "XXX XXX XXX",
        placeholder: "599 123 456",
        maxLength: 9
      };
      
      processedCountries.push(palestine);
      
      // Sort all countries alphabetically
      processedCountries.sort((a, b) => a.name.localeCompare(b.name));
      
      return processedCountries;
    } catch (error) {
      console.error('Error fetching countries:', error);
      // Return fallback countries if API fails
      return this.getFallbackCountries();
    }
  },

  // Fallback countries in case API fails
  getFallbackCountries(): Country[] {
    return [
      { name: "United Arab Emirates", code: "AE", dialCode: "+971", flag: "🇦🇪", format: "XX XXX XXXX", placeholder: "50 123 4567", maxLength: 9 },
      { name: "United States", code: "US", dialCode: "+1", flag: "🇺🇸", format: "(XXX) XXX-XXXX", placeholder: "(555) 123-4567", maxLength: 10 },
      { name: "United Kingdom", code: "GB", dialCode: "+44", flag: "🇬🇧", format: "XXXX XXX XXXX", placeholder: "7400 123456", maxLength: 10 },
      { name: "India", code: "IN", dialCode: "+91", flag: "🇮🇳", format: "XXXXX XXXXX", placeholder: "98765 43210", maxLength: 10 },
      { name: "China", code: "CN", dialCode: "+86", flag: "🇨🇳", format: "XXX XXXX XXXX", placeholder: "138 0013 8000", maxLength: 11 },
      { name: "Germany", code: "DE", dialCode: "+49", flag: "🇩🇪", format: "XXX XXXXXXXX", placeholder: "151 12345678", maxLength: 11 },
      { name: "France", code: "FR", dialCode: "+33", flag: "🇫🇷", format: "X XX XX XX XX", placeholder: "6 12 34 56 78", maxLength: 10 },
      { name: "Italy", code: "IT", dialCode: "+39", flag: "🇮🇹", format: "XXX XXX XXXX", placeholder: "320 123 4567", maxLength: 10 },
      { name: "Spain", code: "ES", dialCode: "+34", flag: "🇪🇸", format: "XXX XX XX XX", placeholder: "612 34 56 78", maxLength: 9 },
      { name: "Canada", code: "CA", dialCode: "+1", flag: "🇨🇦", format: "(XXX) XXX-XXXX", placeholder: "(416) 123-4567", maxLength: 10 },
      { name: "Australia", code: "AU", dialCode: "+61", flag: "🇦🇺", format: "XXX XXX XXX", placeholder: "412 345 678", maxLength: 9 },
      { name: "Japan", code: "JP", dialCode: "+81", flag: "🇯🇵", format: "XX-XXXX-XXXX", placeholder: "90-1234-5678", maxLength: 11 },
      { name: "South Korea", code: "KR", dialCode: "+82", flag: "🇰🇷", format: "XX-XXXX-XXXX", placeholder: "10-1234-5678", maxLength: 11 },
      { name: "Brazil", code: "BR", dialCode: "+55", flag: "🇧🇷", format: "(XX) XXXXX-XXXX", placeholder: "(11) 91234-5678", maxLength: 11 },
      { name: "Russia", code: "RU", dialCode: "+7", flag: "🇷🇺", format: "XXX XXX-XX-XX", placeholder: "912 345-67-89", maxLength: 10 },
      { name: "Saudi Arabia", code: "SA", dialCode: "+966", flag: "🇸🇦", format: "XX XXX XXXX", placeholder: "50 123 4567", maxLength: 9 },
      { name: "Egypt", code: "EG", dialCode: "+20", flag: "🇪🇬", format: "XXX XXX XXXX", placeholder: "100 123 4567", maxLength: 10 },
      { name: "Turkey", code: "TR", dialCode: "+90", flag: "🇹🇷", format: "XXX XXX XX XX", placeholder: "532 123 45 67", maxLength: 10 },
      { name: "Pakistan", code: "PK", dialCode: "+92", flag: "🇵🇰", format: "XXX XXXXXXX", placeholder: "301 2345678", maxLength: 10 },
      { name: "Bangladesh", code: "BD", dialCode: "+880", flag: "🇧🇩", format: "XXXX XXXXXX", placeholder: "1812 345678", maxLength: 10 },
      { name: "Iran", code: "IR", dialCode: "+98", flag: "🇮🇷", format: "XXX XXX XXXX", placeholder: "912 123 4567", maxLength: 10 },
      { name: "Iraq", code: "IQ", dialCode: "+964", flag: "🇮🇶", format: "XXX XXX XXXX", placeholder: "790 123 4567", maxLength: 10 },
      { name: "Palestine", code: "PS", dialCode: "+970", flag: "��", format: "XXX XXX XXX", placeholder: "599 123 456", maxLength: 9 },
      { name: "Jordan", code: "JO", dialCode: "+962", flag: "🇯🇴", format: "XX XXX XXXX", placeholder: "77 123 4567", maxLength: 9 },
      { name: "Kuwait", code: "KW", dialCode: "+965", flag: "🇰🇼", format: "XXXX XXXX", placeholder: "5012 3456", maxLength: 8 },
      { name: "Lebanon", code: "LB", dialCode: "+961", flag: "🇱🇧", format: "XX XXX XXX", placeholder: "71 123 456", maxLength: 8 },
      { name: "Oman", code: "OM", dialCode: "+968", flag: "🇴🇲", format: "XXXX XXXX", placeholder: "9123 4567", maxLength: 8 },
      { name: "Qatar", code: "QA", dialCode: "+974", flag: "🇶🇦", format: "XXXX XXXX", placeholder: "3312 3456", maxLength: 8 },
      { name: "Bahrain", code: "BH", dialCode: "+973", flag: "🇧🇭", format: "XXXX XXXX", placeholder: "3612 3456", maxLength: 8 },
      { name: "Malaysia", code: "MY", dialCode: "+60", flag: "🇲🇾", format: "XX-XXX XXXX", placeholder: "12-345 6789", maxLength: 9 },
      { name: "Singapore", code: "SG", dialCode: "+65", flag: "🇸🇬", format: "XXXX XXXX", placeholder: "8123 4567", maxLength: 8 },
      { name: "Thailand", code: "TH", dialCode: "+66", flag: "🇹🇭", format: "XX XXX XXXX", placeholder: "81 234 5678", maxLength: 9 },
      { name: "Vietnam", code: "VN", dialCode: "+84", flag: "🇻🇳", format: "XXX XXX XXXX", placeholder: "912 345 678", maxLength: 9 },
      { name: "Philippines", code: "PH", dialCode: "+63", flag: "🇵🇭", format: "XXX XXX XXXX", placeholder: "917 123 4567", maxLength: 10 },
      { name: "Indonesia", code: "ID", dialCode: "+62", flag: "🇮🇩", format: "XXX-XXXX-XXXX", placeholder: "812-3456-7890", maxLength: 11 },
    ];
  },

  // Get country by dial code
  getCountryByDialCode(countries: Country[], dialCode: string): Country | undefined {
    return countries.find(country => country.dialCode === dialCode);
  },

  // Get country by country code
  getCountryByCode(countries: Country[], code: string): Country | undefined {
    return countries.find(country => country.code === code);
  },

  // Search countries by name or dial code
  searchCountries(countries: Country[], query: string): Country[] {
    const searchTerm = query.toLowerCase();
    return countries.filter(country => 
      country.name.toLowerCase().includes(searchTerm) ||
      country.dialCode.includes(searchTerm) ||
      country.code.toLowerCase().includes(searchTerm)
    );
  },

  // Format phone number according to country format
  formatPhoneNumber(phoneNumber: string, country: Country): string {
    if (!country) return phoneNumber;

    // Remove all non-digits
    const digits = phoneNumber.replace(/\D/g, '');
    
    // Apply formatting based on country format
    let formatted = '';
    let digitIndex = 0;
    
    for (let i = 0; i < country.format.length && digitIndex < digits.length; i++) {
      if (country.format[i] === 'X') {
        formatted += digits[digitIndex];
        digitIndex++;
      } else {
        formatted += country.format[i];
      }
    }
    
    return formatted;
  },

  // Validate phone number length
  isValidPhoneLength(phoneNumber: string, country: Country): boolean {
    if (!country) return true;
    
    const digits = phoneNumber.replace(/\D/g, '');
    return digits.length === country.maxLength;
  },

  // Get popular countries for quick access
  getPopularCountries(countries: Country[]): Country[] {
    const popularDialCodes = ['+971', '+1', '+44', '+91', '+86', '+49', '+33', '+39', '+34', '+7'];
    return countries.filter(country => popularDialCodes.includes(country.dialCode));
  }
};
